import React from 'react';
import Modal from '@mui/material/Modal';
import DeleteIcon from '@mui/icons-material/Delete';
import Button from '@mui/material/Button';
import { Box, CircularProgress, Divider, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { STATUS } from '../../utils/status';
import { setUpdateUsersStatus } from '../../redux/slices/userSlice/addUserSlice';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/store/appStore';

interface DeleteModalProps {
  open: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  deleteText?: string;
  isButtonLoading?: boolean;
  reviewListText?: any
  confirmationMessage?: string;
}

const DeleteModal: React.FC<DeleteModalProps> = ({ open, onCancel, onConfirm, deleteText = 'Delete', confirmationMessage, isButtonLoading = false, reviewListText }) => {
  return (
    <Modal
      open={open}
      onClose={onCancel}
      aria-labelledby="delete-modal-title"
      aria-describedby="delete-modal-description"
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          bgcolor: 'white',
          p: 3,
          borderRadius: 2,
          boxShadow: 24,
          width: 'auto',
          maxWidth: '400px',
          minWidth: '300px',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography
            variant="h6"
            id="delete-modal-title"
            sx={{
              fontFamily: 'Montserrat',
              fontWeight: 600,
              fontSize: '16px',
              lineHeight: '20px',
              color: '#3D85C6',
              wordBreak: 'break-word',
            }}
          >
            {deleteText}?
          </Typography>
          <CloseIcon onClick={onCancel} sx={{ color: '#3D85C6', cursor: 'pointer' }} />
        </Box>

        <Divider sx={{ mb: 2 }} />
        <Typography
          id="delete-modal-description"
          sx={{
            fontFamily: 'Montserrat',
            fontWeight: 500,
            fontSize: '14px',
            lineHeight: '22px',
            color: '#3D85C6',
            mt: 2,
            wordBreak: 'break-word',
          }}
        >
          {confirmationMessage
            ? confirmationMessage
            : `Are you sure you want to ${deleteText?.toLowerCase()} it?`}
        </Typography>

        <Typography
          sx={{
            fontFamily: 'Montserrat',
            fontWeight: 500,
            fontSize: '14px',
            lineHeight: '22px',
            color: '#3D85C6',
            mt: 1,
            wordBreak: 'break-word',
          }}
        >
          {reviewListText || "You can't undo this change later."}
        </Typography>

        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button
            onClick={onCancel}
            sx={{
              minHeight: '40px',
              backgroundColor: '#F7F9FA',
              borderRadius: '8px',
              px: 3,
            }}
          >
            <Typography
              sx={{
                fontFamily: 'Montserrat',
                fontWeight: 600,
                fontSize: '14px',
                color: '#10383D',
                textTransform: 'none',
              }}
            >
              Cancel
            </Typography>
          </Button>

          <Button
            variant="contained"
            color="error"
            onClick={onConfirm}
            disabled={isButtonLoading}
            startIcon={isButtonLoading ? <CircularProgress size={17} /> : deleteText === 'Delete' ? <DeleteIcon /> : null}
            sx={{
              minHeight: '40px',
              backgroundColor: deleteText ? '#3D85C6' : '#C00F00',
              borderRadius: '8px',
              px: 3,
              '&:hover': {
                backgroundColor: deleteText ? '#3D85C6' : '#C00F00',
              },
            }}
          >
            <Typography
              sx={{
                fontFamily: 'Montserrat',
                fontWeight: 600,
                fontSize: '14px',
                color: '#FFFFFF',
                textTransform: 'none',
              }}
            >
              Confirm {deleteText}
            </Typography>
          </Button>
        </Box>
      </Box>
    </Modal>
  );
}

export default DeleteModal;
